import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { useTranslation } from 'react-i18next'

import {
  Box,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'

import { RegionLoading } from '~/components/common/RegionLoading'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
import type { IntradayOrderRow } from '~/hooks/bidding/useListBiddingIntradayOrders'
import { convertAreaLabel } from '~/models/area'
import { formatJstDateTimeHm } from '~/models/dateTime'
import {
  KOMA_DIVIDER_ROW_CSS,
  komaListDividerFlags,
} from '~/models/komaDivider'
import { BiddingIntradayWithdrawStateTag } from '~/pages/IntradayBidding/components/BiddingIntradayWithdrawStateTag'
import { IntradayBidStatusCell } from '~/pages/IntradayBidding/components/IntradayBidStatusCell'
import {
  NoValue,
  NumberOrNoValue,
  TextOrNoValue,
} from '~/pages/IntradayBidding/components/IntradayCellValue'
import { convertIntradayBidFromOrder } from '~/pages/IntradayBidding/models/convertIntradayBidsFromOrders'

type Props = {
  rows: IntradayOrderRow[]
  isLoading: boolean
}

function formatProtoDate(
  d: { year: number; month: number; day: number } | undefined,
): string {
  if (d === undefined) return '—'
  const mm = String(d.month).padStart(2, '0')
  const dd = String(d.day).padStart(2, '0')
  return `${d.year}/${mm}/${dd}`
}

/** proto Timestamp → 日時表示。 書式は AC-BIN-047 の既定 `yyyy/MM/dd HH:mm`。 */
function formatTimestamp(ts: Timestamp): string {
  return formatJstDateTimeHm(timestampDate(ts))
}

/**
 * 時間前注文 一覧テーブル (= 入札履歴タブ、 読み取り専用、 AC-BIN-024)。
 *
 * 列は AC-BIN-024 のとおり: 最終約定時刻 / エリア / 売/買 / 約定価格 / 約定量 / 残数量 /
 * ステータス / 受渡契約コード / ワークフローID。 1 行 = 1 札。 約定価格・約定量・残数量・
 * 最終約定時刻・受渡契約コードは backend が札ごとに算出して返す値 (約定価格は加重平均、
 * 約定量は札の合計、 残数量は注文量 − 約定量) をそのまま出す。
 *
 * ステータスは入札一覧と同じ 1 列の合成ステータス (AC-BIN-035。 `IntradayBidStatusCell`)。
 * 取消処理中・取消失敗 (AC-BIN-008b) は別軸のチップで併記する。
 */
export const IntradayOrderTable = ({
  rows,
  isLoading,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  if (isLoading) {
    return <RegionLoading isLoading />
  }

  if (rows.length === 0) {
    return (
      <Box padding={4} textAlign="center">
        <Text color="Text.lowEmphasis">
          {t('page:biddingIntraday.list.empty')}
        </Text>
      </Box>
    )
  }

  // 3 時間 (6 コマ) ごとの区切り線 (conventions.md §48 コマの表の区切り線)。 入札履歴は受渡日を
  // またいで並ぶので、 受渡日が変わる所には引かず、 受渡日ごとに境目をまたぐ行の下に 1 本引く。
  const komaDividerFlags = komaListDividerFlags(
    rows,
    (row) => row.timeCode,
    (row) => formatProtoDate(row.targetDate),
  )
  return (
    <Box overflowX="auto">
      {/* design `.ecl-table` td = font-size md (14px)。 ecloud table size="sm" は
          textStyle sm (12px) で一段小さいため、 root に fontSize="md" を当てて
          td/th へ継承させる (= 共通 MasterDataTable と同じ 14px に揃える)。 */}
      <TableRoot fontSize="md" size="sm" variant="line">
        <TableHeader>
          <TableRow>
            {/* [design 差異] ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断、
                design/CLAUDE.md「一覧テーブルのステータス列はデータ列の最左」)。 受渡日・コマは
                AC-BIN-024 の列に無いが、 無いとどの札か読めないのでステータスの次に置く (#9585)。 */}
            <TableColumnHeader>
              {t('page:biddingIntraday.column.status')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.column.targetDate')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.column.timeCode')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.column.lastMatchedAt')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.column.area')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="center">
              {t('page:biddingIntraday.column.direction')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingIntraday.column.matchedPriceYenPerKwh')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingIntraday.column.matchedVolumeMw')}
            </TableColumnHeader>
            <TableColumnHeader textAlign="end">
              {t('page:biddingIntraday.column.remainingVolumeMw')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.column.deliveryContractCd')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('page:biddingIntraday.column.workflowId')}
            </TableColumnHeader>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => {
            const bid = convertIntradayBidFromOrder(row)
            return (
              <TableRow
                key={row.id}
                css={
                  komaDividerFlags[rowIndex] === true
                    ? KOMA_DIVIDER_ROW_CSS
                    : undefined
                }
              >
                <TableCell>
                  <IntradayBidStatusCell bid={bid} />
                  {/* AC-BIN-008b: 取消処理中・取消失敗はステータスバッジと別軸のチップで併記
                      (取消失敗は札が終わった後も残す)。 */}
                  <BiddingIntradayWithdrawStateTag
                    failureReason={bid.withdrawFailureReason}
                    state={bid.withdrawState}
                  />
                </TableCell>
                <TableCell>{formatProtoDate(row.targetDate)}</TableCell>
                <TableCell>{bid.timeCode}</TableCell>
                <TableCell css={{ fontVariantNumeric: 'tabular-nums' }}>
                  {bid.lastMatchedAt === undefined ? (
                    <NoValue />
                  ) : (
                    formatTimestamp(bid.lastMatchedAt)
                  )}
                </TableCell>
                <TableCell>{convertAreaLabel(bid.areaCode) || '—'}</TableCell>
                <TableCell textAlign="center">
                  <TradeSideLabel side={bid.direction} />
                </TableCell>
                {/* 約定価格は約定が複数ある札では加重平均 (backend が約定同期で算出済み)。
                    価格・数量の桁は入札追加タブの一覧 (IntradayBidTable) と同じ部品・同じ桁。 */}
                <TableCell
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <NumberOrNoValue
                    digits={2}
                    value={bid.matchedPriceYenPerKwh}
                  />
                </TableCell>
                <TableCell
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <NumberOrNoValue digits={1} value={bid.execVolumeMw} />
                </TableCell>
                <TableCell
                  textAlign="end"
                  css={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  <NumberOrNoValue digits={1} value={bid.remainingVolumeMw} />
                </TableCell>
                {/* 受渡契約コードを保存していない札は「—」 (AC-BIN-024)。 */}
                <TableCell>
                  <TextOrNoValue value={bid.deliveryContractCd} />
                </TableCell>
                {/* AC-BIN-003: ワークフローを経由せずに作った入札 (workflowId 未設定) は空表示。 */}
                <TableCell>{bid.workflowId ?? ''}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </TableRoot>
    </Box>
  )
}
